import { useState } from "react";

const PLATFORMS = [
  { value: "instagram", label: "Instagram", icon: "📸" },
  { value: "tiktok", label: "TikTok", icon: "🎵" },
  { value: "youtube", label: "YouTube", icon: "▶️" },
  { value: "facebook", label: "Facebook", icon: "👥" },
  { value: "x", label: "X (Twitter)", icon: "✖️" },
  { value: "podcast", label: "Podcast", icon: "🎙️" },
  { value: "linkedin", label: "LinkedIn", icon: "💼" },
  { value: "truth_social", label: "Truth Social", icon: "🇺🇸" },
  { value: "other", label: "Other", icon: "🌐" },
];

const NICHES = [
  "Faith & Christianity", "Christian Wellness", "Mental Health & Faith",
  "Athletes & Sports Ministry", "Christian Parenting & Family",
  "Biblical Mindset & Motivation", "Church & Ministry", "Christian Lifestyle",
  "Faith-Based Business", "Mindfulness & Spirituality", "Other"
];

const T = {
  navy: '#1e3a5f', accent: '#4f46e5', accentLight: '#eef2ff', accentBorder: '#c7d2fe',
  green: '#059669', greenLight: '#ecfdf5', greenBorder: '#a7f3d0',
  body: '#475569', bodyLight: '#64748b', cardBg: '#ffffff', cardBorder: '#e2e8f0',
  red: '#dc2626', redLight: '#fef2f2', redBorder: '#fecaca',
};

export default function AmbassadorForm() {
  const [form, setForm] = useState({
    name: '', email: '', platform: '', profile_url: '', follower_count: '',
    content_niche: '', secondary_platform: '', secondary_url: '',
    secondary_followers: '', audience_description: '',
  });
  const [showSecondary, setShowSecondary] = useState(false);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) return 'Please enter your full name.';
    if (!form.email.trim() || !form.email.includes('@')) return 'Please enter a valid email address.';
    if (!form.platform) return 'Please select your primary platform.';
    if (!form.profile_url.trim()) return 'Please enter your profile or channel URL.';
    if (!form.follower_count || parseInt(form.follower_count) < 1) return 'Please enter your follower or subscriber count.';
    if (parseInt(form.follower_count) < 1000) return 'Minimum 1,000 followers or subscribers required to apply.';
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setStatus('submitting');
    const payload = {
      name: form.name.trim(), email: form.email.trim(), platform: form.platform,
      profile_url: form.profile_url.trim(), follower_count: parseInt(form.follower_count),
      content_niche: form.content_niche || null,
      secondary_platform: form.secondary_platform || null,
      secondary_url: form.secondary_url || null,
      secondary_followers: form.secondary_followers ? parseInt(form.secondary_followers) : null,
      audience_description: form.audience_description || null,
    };
    try {
      const r = await fetch(`${window.location.origin}/api/v1/ambassador-apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const d = await r.json();
        setError(d.detail || 'Submission failed. Please try again.');
        setStatus('error');
      } else {
        setStatus('success');
      }
    } catch {
      setError('Cannot connect to server. Please try again.');
      setStatus('error');
    }
  };

  const inputStyle = {
    width: '100%', padding: '11px 14px', borderRadius: '10px',
    border: `1px solid ${T.cardBorder}`, fontSize: '15px', color: T.navy,
    background: '#fff', boxSizing: 'border-box', outline: 'none',
    fontFamily: "'Libre Franklin', sans-serif",
  };

  const labelStyle = {
    fontSize: '11px', fontWeight: '700', color: T.bodyLight,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    display: 'block', marginBottom: '5px',
  };

  if (status === 'success') {
    return (
      <div style={{ background: T.greenLight, border: `1px solid ${T.greenBorder}`, borderRadius: '16px', padding: '40px 32px', textAlign: 'center', maxWidth: '580px', margin: '0 auto' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
        <h3 style={{ fontSize: '22px', fontWeight: '700', color: T.navy, margin: '0 0 12px' }}>Application Received!</h3>
        <p style={{ fontSize: '15px', color: T.body, lineHeight: 1.7, margin: '0 0 20px' }}>
          Thank you for applying. We will review your profile and get back to you within <strong>1–2 business days</strong>. Check your email for a confirmation.
        </p>
        <p style={{ fontSize: '13px', color: T.bodyLight, margin: 0 }}>
          Questions? Email <a href="mailto:partnerships@freehappyone.com" style={{ color: T.accent }}>partnerships@freehappyone.com</a>
        </p>
      </div>
    );
  }

  return (
    <div style={{ background: T.cardBg, borderRadius: '20px', padding: '36px 32px', boxShadow: '0 4px 32px rgba(30,58,95,0.10)', maxWidth: '580px', margin: '0 auto', fontFamily: "'Libre Franklin', sans-serif" }}>
      <div style={{ marginBottom: '28px' }}>
        <div style={{ display: 'inline-block', padding: '3px 12px', borderRadius: '16px', background: T.accentLight, border: `1px solid ${T.accentBorder}`, fontSize: '11px', fontWeight: '700', color: T.accent, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px' }}>
          Ambassador Application
        </div>
        <h3 style={{ fontSize: '22px', fontWeight: '700', color: T.navy, margin: '0 0 8px' }}>Apply for Free Access</h3>
        <p style={{ fontSize: '14px', color: T.bodyLight, margin: 0, lineHeight: 1.6 }}>
          Tell us about your platform. We will verify your profile and send your access code within 1–2 business days.
        </p>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: T.redLight, border: `1px solid ${T.redBorder}`, borderRadius: '8px', color: T.red, fontSize: '13px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        <div>
          <label style={labelStyle}>Full Name *</label>
          <input type="text" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Your name" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Email Address *</label>
          <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@email.com" style={inputStyle} />
        </div>
      </div>

      <div style={{ marginBottom: '14px' }}>
        <label style={labelStyle}>Primary Platform *</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {PLATFORMS.map(p => (
            <button key={p.value} onClick={() => set('platform', p.value)}
              style={{ padding: '8px 14px', borderRadius: '20px', cursor: 'pointer', border: `1px solid ${form.platform === p.value ? T.accent : T.cardBorder}`, background: form.platform === p.value ? T.accentLight : '#fff', color: form.platform === p.value ? T.accent : T.body, fontSize: '13px', fontWeight: form.platform === p.value ? '700' : '400', fontFamily: "'Libre Franklin', sans-serif" }}>
              {p.icon} {p.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: '14px' }}>
        <label style={labelStyle}>Profile / Channel URL *</label>
        <input type="url" value={form.profile_url} onChange={e => set('profile_url', e.target.value)} placeholder="https://instagram.com/yourhandle" style={inputStyle} />
        <span style={{ fontSize: '11px', color: T.bodyLight, marginTop: '4px', display: 'block' }}>We use this to verify your account. Must be a public profile.</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
        <div>
          <label style={labelStyle}>Followers / Subscribers *</label>
          <input type="number" value={form.follower_count} onChange={e => set('follower_count', e.target.value)} placeholder="e.g. 25000" min="1000" style={inputStyle} />
          <span style={{ fontSize: '11px', color: T.bodyLight, marginTop: '4px', display: 'block' }}>Minimum 1,000</span>
        </div>
        <div>
          <label style={labelStyle}>Content Niche</label>
          <select value={form.content_niche} onChange={e => set('content_niche', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select niche...</option>
            {NICHES.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: '14px' }}>
        <label style={labelStyle}>Tell us about your audience</label>
        <textarea value={form.audience_description} onChange={e => set('audience_description', e.target.value)}
          placeholder="Describe who follows you, your content style, and why FHO would resonate with your community..."
          rows={3} style={{ ...inputStyle, resize: 'vertical', minHeight: '80px' }} />
      </div>

      <div style={{ marginBottom: '20px' }}>
        <button onClick={() => setShowSecondary(s => !s)}
          style={{ background: 'none', border: 'none', color: T.accent, fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: 0, fontFamily: "'Libre Franklin', sans-serif" }}>
          {showSecondary ? '− Remove secondary platform' : '+ Add a second platform (optional)'}
        </button>
        {showSecondary && (
          <div style={{ marginTop: '14px', padding: '16px', background: '#f8fafc', borderRadius: '10px', border: `1px solid ${T.cardBorder}` }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={labelStyle}>Platform</label>
                <select value={form.secondary_platform} onChange={e => set('secondary_platform', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value="">Select...</option>
                  {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.icon} {p.label}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Followers</label>
                <input type="number" value={form.secondary_followers} onChange={e => set('secondary_followers', e.target.value)} placeholder="e.g. 10000" style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Profile URL</label>
              <input type="url" value={form.secondary_url} onChange={e => set('secondary_url', e.target.value)} placeholder="https://youtube.com/@yourchannel" style={inputStyle} />
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: '12px 16px', background: T.accentLight, borderRadius: '10px', border: `1px solid ${T.accentBorder}`, fontSize: '12px', color: T.accent, marginBottom: '20px', lineHeight: 1.6 }}>
        <strong>Qualification:</strong> Any platform with 1,000+ followers or subscribers. We welcome Instagram, TikTok, YouTube, Podcasts, Facebook, X, LinkedIn, and more.
      </div>

      <button onClick={handleSubmit} disabled={status === 'submitting'}
        style={{ width: '100%', padding: '15px', borderRadius: '12px', border: 'none', background: status === 'submitting' ? T.bodyLight : T.accent, color: '#fff', fontSize: '16px', fontWeight: '700', cursor: status === 'submitting' ? 'not-allowed' : 'pointer', fontFamily: "'Libre Franklin', sans-serif", transition: 'background 0.2s' }}>
        {status === 'submitting' ? 'Submitting...' : 'Apply for Free Access →'}
      </button>

      <p style={{ textAlign: 'center', fontSize: '12px', color: T.bodyLight, marginTop: '12px', marginBottom: 0 }}>
        No payment required. Free access is exclusively for approved creators.
      </p>
    </div>
  );
}
